import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Form, FormField, Input, ModalDialog, Numeric, Select, TranslatableField } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { SUBSTRATE_CATEGORIES, SubstrateCategory } from "../../../../http/screwConnector/types/common";
import {
  InsertTimberMaterialBody,
  TimberMaterial,
  UpdateTimberMaterialBody,
} from "../../../../http/screwConnector/types/timberMaterials";
import useScrewConnectorTimberMaterialsApi from "../../../../http/screwConnector/useAdminTimberMaterials";
import { translatableFieldProps } from "../../../../utils/translatableField";

interface TimberMaterialFormBody {
  materialCategory: SubstrateCategory | null;
  species: string | null;
  speciesIt: string | null;
  speciesDe: string | null;
  speciesEs: string | null;
  speciesFr: string | null;
  class: string | null;
  characteristicDensity: number | null;
  meanDensity: number | null;
  compressionStrength0: number | null;
  compressionStrength90: number | null;
  rollingShearStrength: number | null;
  shearStrength: number | null;
  standardReference: string | null;
  visibility: TimberMaterial["visibility"];
  order: number | null;
}

interface TimberMaterialFormProps {
  timberMaterial?: TimberMaterial;
  onClose: () => void;
}

const TimberMaterialForm: FC<TimberMaterialFormProps> = ({ timberMaterial, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const dispatch = useContext(NotificationDispatch);
  const timberMaterialsApi = useScrewConnectorTimberMaterialsApi();

  const substrateCategoryLabels: Record<SubstrateCategory, string> = t.substrateCategories;

  const defaultValues: TimberMaterialFormBody = {
    materialCategory: timberMaterial?.materialCategory ?? null,
    species: timberMaterial?.species ?? null,
    speciesIt: timberMaterial?.speciesIt ?? null,
    speciesDe: timberMaterial?.speciesDe ?? null,
    speciesEs: timberMaterial?.speciesEs ?? null,
    speciesFr: timberMaterial?.speciesFr ?? null,
    class: timberMaterial?.class ?? null,
    characteristicDensity: timberMaterial?.characteristicDensity ?? null,
    meanDensity: timberMaterial?.meanDensity ?? null,
    compressionStrength0: timberMaterial?.compressionStrength0 ?? null,
    compressionStrength90: timberMaterial?.compressionStrength90 ?? null,
    rollingShearStrength: timberMaterial?.rollingShearStrength ?? null,
    shearStrength: timberMaterial?.shearStrength ?? null,
    standardReference: timberMaterial?.standardReference ?? null,
    visibility: timberMaterial?.visibility ?? "public",
    order: timberMaterial?.order ?? null,
  };

  const onSubmit = () => async (formData: TimberMaterialFormBody) => {
    if (!formData.materialCategory || !formData.class) {
      return;
    }

    const body: InsertTimberMaterialBody | UpdateTimberMaterialBody = {
      materialCategory: formData.materialCategory,
      species: formData.species?.trim() || null,
      speciesIt: formData.speciesIt || null,
      speciesDe: formData.speciesDe || null,
      speciesEs: formData.speciesEs || null,
      speciesFr: formData.speciesFr || null,
      class: formData.class,
      characteristicDensity: formData.characteristicDensity,
      meanDensity: formData.meanDensity,
      compressionStrength0: formData.compressionStrength0,
      compressionStrength90: formData.compressionStrength90,
      rollingShearStrength: formData.rollingShearStrength,
      shearStrength: formData.shearStrength,
      standardReference: formData.standardReference?.trim() || null,
      visibility: formData.visibility,
      order: formData.order ?? 0,
    };

    try {
      if (timberMaterial) {
        await timberMaterialsApi.updateTimberMaterial(timberMaterial.id, body);
      } else {
        await timberMaterialsApi.insertTimberMaterial(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        timberMaterial
          ? `${i18n.translation.common.edit} ${t.timberMaterials.singular}`
          : `${i18n.translation.common.add} ${t.timberMaterials.singular}`
      }
    >
      <Form<TimberMaterialFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [timberMaterial]);

          useEffect(() => {
            register("speciesIt");
            register("speciesDe");
            register("speciesEs");
            register("speciesFr");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="materialCategory"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.materialCategory} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<{ value: SubstrateCategory; label: string }, false>
                        inputId={labelId}
                        options={SUBSTRATE_CATEGORIES.map((value) => ({ value, label: substrateCategoryLabels[value] }))}
                        value={field.value ? { value: field.value, label: substrateCategoryLabels[field.value] } : null}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option?.value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <TranslatableField
                {...translatableFieldProps("species", values, setValue, i18n.translation)}
                dialogTitle={t.timberMaterials.fields.species}
              >
                <FormField label={t.timberMaterials.fields.species} error={errors.species} isOptional>
                  {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("species")} />}
                </FormField>
              </TranslatableField>
              <FormField label={t.timberMaterials.fields.class} error={errors.class} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input id={labelId} isInvalid={isInvalid} {...register("class", { required: !isOptional, minLength: 1 })} />
                )}
              </FormField>
              <Controller
                name="characteristicDensity"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.characteristicDensity} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={1}
                        suffix={" " + t.units.unitKgM3}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="meanDensity"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.meanDensity} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={1}
                        suffix={" " + t.units.unitKgM3}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="compressionStrength0"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.compressionStrength0} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={2}
                        suffix={" " + t.units.unitNMm2}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="compressionStrength90"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.compressionStrength90} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={2}
                        suffix={" " + t.units.unitNMm2}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="rollingShearStrength"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.rollingShearStrength} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={2}
                        suffix={" " + t.units.unitNMm2}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="shearStrength"
                control={control}
                rules={{ min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.timberMaterials.fields.shearStrength} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0}
                        decimalScale={2}
                        suffix={" " + t.units.unitNMm2}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={t.timberMaterials.fields.standardReference} error={errors.standardReference} isOptional>
                {({ labelId, isInvalid }) => <Input id={labelId} isInvalid={isInvalid} {...register("standardReference")} />}
              </FormField>
              <Controller
                name="visibility"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                    {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                  </FormField>
                )}
              />
              <Controller
                name="order"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        decimalScale={0}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default TimberMaterialForm;
